import React, { useEffect, useState } from "react";
import { Check, ChevronDown, ChevronRight, Lightbulb, Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { KnowledgeScope } from "./knowledgeBankStore";
import {
  StarterQuestion,
  hasUnfilledPlaceholder,
  uncoveredStarters,
} from "./knowledgeBankStarters";
import { KnowledgeEntry } from "./types";

interface KnowledgeTierSectionProps {
  scope: KnowledgeScope;
  /** Stable slug for test ids and input ids, e.g. "org" or a practice id. */
  slug: string;
  /** Who these answers are used for, e.g. "All locations" or "Oak Hill only". */
  title: string;
  /** Same thing worded to finish "Save this answer for …". */
  saveTargetLabel: string;
  summary: string;
  /** Entries after any active search; the count reflects the whole group. */
  entries: KnowledgeEntry[];
  totalCount: number;
  isSearching: boolean;
  highlightedId: string | null;
  /** Ids created in this browser session, so the row can show that it is new. */
  sessionAddedIds: ReadonlySet<string>;
  onAdd: (question: string, answer: string) => void;
  onEdit: (entryId: string, question: string, answer: string) => void;
  onRemove: (entryId: string) => void;
  onSaveSummary: (summary: string) => void;
}

/**
 * One group of answers — the set used at every location, or the set used at a
 * single clinic — with the controls to add a question and to rewrite the
 * description of what the group covers.
 */
function KnowledgeTierSection({
  scope,
  slug,
  title,
  saveTargetLabel,
  summary,
  entries,
  totalCount,
  isSearching,
  highlightedId,
  sessionAddedIds,
  onAdd,
  onEdit,
  onRemove,
  onSaveSummary,
}: KnowledgeTierSectionProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [pendingDelete, setPendingDelete] = useState<KnowledgeEntry | null>(null);
  const [isEditingContext, setIsEditingContext] = useState(false);
  const [draftSummary, setDraftSummary] = useState(summary);
  // A group with nothing in it yet is exactly when the starters help most.
  const [showStarters, setShowStarters] = useState(totalCount === 0);

  const [focusAnswerWhenOpen, setFocusAnswerWhenOpen] = useState(false);

  // A saved answer is read to callers verbatim, so a leftover "[address]" would be spoken.
  const hasBlanks = hasUnfilledPlaceholder(question) || hasUnfilledPlaceholder(answer);
  const canSaveEntry = question.trim() !== "" && answer.trim() !== "" && !hasBlanks;
  const editHasBlanks = hasUnfilledPlaceholder(editQuestion) || hasUnfilledPlaceholder(editAnswer);
  const canSaveEdit = editQuestion.trim() !== "" && editAnswer.trim() !== "" && !editHasBlanks;
  // While a search is active `entries` is filtered, so only judge coverage on the full group.
  const starters = isSearching ? [] : uncoveredStarters(entries.map((entry) => entry.question));
  const starterCategories = Array.from(new Set(starters.map((starter) => starter.category)));
  // What was just saved belongs where the eye already is, under the add form.
  const orderedEntries = [
    ...entries.filter((entry) => sessionAddedIds.has(entry.id)),
    ...entries.filter((entry) => !sessionAddedIds.has(entry.id)),
  ];

  useEffect(() => {
    if (!isAdding || !focusAnswerWhenOpen) {
      return;
    }
    // The starter list can be long; the form it filled sits above it, off screen.
    const answerInput = document.getElementById(`kb-answer-input-${slug}`);
    answerInput?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    answerInput?.focus();
    setFocusAnswerWhenOpen(false);
  }, [isAdding, focusAnswerWhenOpen, slug]);

  const closeAddForm = () => {
    setIsAdding(false);
    setQuestion("");
    setAnswer("");
  };

  const applyStarter = (starter: StarterQuestion) => {
    setQuestion(starter.question);
    setAnswer(starter.answer);
    setIsAdding(true);
    setFocusAnswerWhenOpen(true);
  };

  const saveEntry = () => {
    if (!canSaveEntry) {
      return;
    }
    onAdd(question.trim(), answer.trim());
    closeAddForm();
  };

  const startEditingContext = () => {
    setDraftSummary(summary);
    setIsEditingContext(true);
  };

  const saveContext = () => {
    const trimmed = draftSummary.trim();
    if (trimmed !== "") {
      onSaveSummary(trimmed);
    }
    setIsEditingContext(false);
  };

  return (
    <Card className="gap-3 py-4" data-testid={`kb-section-${slug}`}>
      <CardHeader className="px-4">
        <CardTitle className="text-sm flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={scope.tier === "ORG" ? "secondary" : "outline"}
              className="text-[10px]"
            >
              {title}
            </Badge>
            <span className="text-xs font-normal text-gray-500">
              {totalCount} {totalCount === 1 ? "answer" : "answers"}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              data-testid={`kb-edit-context-${slug}`}
              onClick={
                isEditingContext
                  ? () => setIsEditingContext(false)
                  : startEditingContext
              }
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit description
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              data-testid={`kb-add-entry-${slug}`}
              onClick={isAdding ? closeAddForm : () => setIsAdding(true)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add question
            </Button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 space-y-2">
        {isEditingContext ? (
          <div className="space-y-2 rounded-md border border-brand-100 bg-brand-50 p-3">
            <Label
              htmlFor={`kb-context-input-${slug}`}
              className="text-xs text-gray-700"
            >
              What this group of answers covers
            </Label>
            <p className="text-[10px] text-gray-600" data-testid={`kb-context-hint-${slug}`}>
              A brief overview of your practice that the assistant keeps in mind on
              every call: what you do, the services you offer, and who you serve. A
              few sentences is plenty.
            </p>
            <Textarea
              id={`kb-context-input-${slug}`}
              value={draftSummary}
              onChange={(event) => setDraftSummary(event.target.value)}
              data-testid={`kb-context-input-${slug}`}
              className="text-xs min-h-24 bg-white"
            />
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                className="h-7 text-xs"
                data-testid={`kb-save-context-${slug}`}
                disabled={draftSummary.trim() === ""}
                onClick={saveContext}
              >
                <Check className="h-3 w-3 mr-1" />
                Save description
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setIsEditingContext(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-600" data-testid={`kb-context-${slug}`}>
            {summary}
          </p>
        )}

        {isAdding && (
          <div
            className="space-y-2 rounded-md border border-brand-100 bg-brand-50 p-3"
            data-testid={`kb-add-form-${slug}`}
          >
            <div className="space-y-1">
              <Label
                htmlFor={`kb-question-input-${slug}`}
                className="text-xs text-gray-700"
              >
                Question callers ask
              </Label>
              <Input
                id={`kb-question-input-${slug}`}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                data-testid={`kb-question-input-${slug}`}
                placeholder="e.g. Do you offer sibling discounts?"
                className="h-8 text-xs bg-white"
              />
            </div>
            <div className="space-y-1">
              <Label
                htmlFor={`kb-answer-input-${slug}`}
                className="text-xs text-gray-700"
              >
                Answer the assistant should give
              </Label>
              <Textarea
                id={`kb-answer-input-${slug}`}
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                data-testid={`kb-answer-input-${slug}`}
                placeholder="Write the answer exactly as you would want it said on a call."
                className="text-xs min-h-20 bg-white"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                className="h-7 text-xs"
                data-testid={`kb-save-entry-${slug}`}
                disabled={!canSaveEntry}
                onClick={saveEntry}
              >
                <Check className="h-3 w-3 mr-1" />
                Save this answer for {saveTargetLabel}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={closeAddForm}
              >
                Cancel
              </Button>
            </div>
            <p className="text-[10px] text-gray-600" data-testid={`kb-add-hint-${slug}`}>
              {hasBlanks
                ? "Replace the [bracketed] parts with your own details before saving."
                : "The assistant can use this answer on future calls. Calls already handled stay exactly as they are."}
            </p>
          </div>
        )}

        {entries.length === 0 ? (
          <p className="text-xs text-gray-500 italic pt-1">
            {isSearching
              ? "No answers in this group match your search."
              : "No answers yet — add the first question this group should cover."}
          </p>
        ) : (
          orderedEntries.map((entry) => {
            const isAdded = sessionAddedIds.has(entry.id);
            const isEditing = editingId === entry.id;
            return (
              <div
                key={entry.id}
                id={entry.id}
                data-testid={`kb-entry-${entry.id}`}
                aria-current={entry.id === highlightedId ? "true" : undefined}
                className={`rounded-md border px-3 py-2 ${
                  entry.id === highlightedId
                    ? "border-brand bg-brand-50 ring-2 ring-brand-100"
                    : "border-border bg-white"
                }`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <Input
                      value={editQuestion}
                      onChange={(event) => setEditQuestion(event.target.value)}
                      data-testid={`kb-edit-question-${entry.id}`}
                      aria-label="Question callers ask"
                      className="h-8 text-xs"
                    />
                    <Textarea
                      value={editAnswer}
                      onChange={(event) => setEditAnswer(event.target.value)}
                      data-testid={`kb-edit-answer-${entry.id}`}
                      aria-label="Answer the assistant should give"
                      className="text-xs min-h-20"
                    />
                    {editHasBlanks && (
                      <p className="text-[10px] text-gray-600" data-testid={`kb-edit-hint-${entry.id}`}>
                        Replace the [bracketed] parts with your own details before saving.
                      </p>
                    )}
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        data-testid={`kb-save-edit-${entry.id}`}
                        disabled={!canSaveEdit}
                        onClick={() => {
                          onEdit(entry.id, editQuestion.trim(), editAnswer.trim());
                          setEditingId(null);
                        }}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900">
                        “{entry.question}”
                      </p>
                      <span className="flex items-center shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-xs text-gray-500"
                          aria-label={`Edit entry: ${entry.question}`}
                          data-testid={`kb-edit-entry-${entry.id}`}
                          onClick={() => {
                            setEditingId(entry.id);
                            setEditQuestion(entry.question);
                            setEditAnswer(entry.answer);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-xs text-gray-500 hover:text-red-700"
                          aria-label={`Remove entry: ${entry.question}`}
                          data-testid={`kb-remove-${entry.id}`}
                          onClick={() => setPendingDelete(entry)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{entry.answer}</p>
                    {isAdded && (
                      <Badge
                        variant="outline"
                        className="mt-2 text-[9px] border-brand-100 text-brand"
                      >
                        Added in this session
                      </Badge>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}

        {starters.length > 0 && (
          <div
            className="rounded-md border border-dashed border-gray-200 p-3"
            data-testid={`kb-starters-${slug}`}
          >
            <button
              type="button"
              className="flex w-full items-center gap-1 text-left text-xs font-medium text-gray-700"
              data-testid={`kb-starters-toggle-${slug}`}
              aria-expanded={showStarters}
              onClick={() => setShowStarters((current) => !current)}
            >
              {showStarters ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              <Lightbulb className="h-3 w-3 text-amber-500" />
              Suggested questions to cover ({starters.length})
            </button>
            {showStarters && (
              <div className="mt-2 space-y-3">
                <p className="text-[10px] text-gray-600">
                  Questions callers ask most. Pick one, replace the [bracketed]
                  parts with your details for {saveTargetLabel}, and save.
                </p>
                {starterCategories.map((category) => (
                  <div key={category} className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      {category}
                    </p>
                    {starters
                      .filter((starter) => starter.category === category)
                      .map((starter) => (
                        <div
                          key={starter.id}
                          className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-gray-50"
                        >
                          <span className="text-xs text-gray-800">{starter.question}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 shrink-0 text-xs"
                            data-testid={`kb-starter-${slug}-${starter.id}`}
                            onClick={() => applyStarter(starter)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Use
                          </Button>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <Dialog
          open={pendingDelete !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingDelete(null);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this question?</DialogTitle>
              <DialogDescription>
                {pendingDelete
                  ? `“${pendingDelete.question}” will be removed. The assistant will take a message instead of answering it.`
                  : ""}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="kb-confirm-delete"
                onClick={() => {
                  if (pendingDelete) {
                    onRemove(pendingDelete.id);
                  }
                  setPendingDelete(null);
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default KnowledgeTierSection;
